import { apiClient } from './client';

const isDev =
  typeof import.meta !== 'undefined' &&
  typeof (import.meta as any).env !== 'undefined' &&
  ((import.meta as any).env.MODE !== 'production');

export interface UploadFileResponse {
  id: string;
  filename: string;
  originalName: string;
  url: string;
  size: number;
  mimeType: string;
}

export interface FileInfo {
  id: string;
  filename: string;
  originalName: string;
  filepath: string;
  url: string;
  size: number;
  mimeType: string;
  uploadedAt: string;
}

export class FileService {
  /**
    * Upload d'un fichier
    */
  async uploadFile(file: File): Promise<UploadFileResponse> {
    const formData = new FormData();
    formData.append('file', file);

    if (isDev) console.log('🔄 Envoi de la requête d\'upload...');

    try {
      const data = await apiClient.post<UploadFileResponse>('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (isDev) console.log('✅ Upload réussi:', data);

      if (!data) {
        throw new Error('Aucune donnée reçue du serveur');
      }

      return data;
    } catch (error) {
      // Garder le log d'erreur pour diagnostic
      console.error('❌ Erreur API client:', error);
      throw error;
    }
  }

  /**
   * Récupération d'un fichier par son nom
   */
  async getFile(filename: string): Promise<Blob> {
    const response = await apiClient.get(`/files/${filename}`, {
      responseType: 'blob',
    });

    return response.data;
  }

  /**
   * Récupération des informations d'un fichier
   */
  async getFileInfo(filename: string): Promise<FileInfo> {
    const response = await apiClient.get(`/files/info/${filename}`);
    return response.data;
  }

  /**
   * Suppression d'un fichier
   */
  async deleteFile(filename: string): Promise<void> {
    await apiClient.delete(`/files/${filename}`);
  }

  /**
   * Upload multiple files
   */
  async uploadMultipleFiles(files: File[]): Promise<UploadFileResponse[]> {
    const uploadPromises = files.map(file => this.uploadFile(file));
    return Promise.all(uploadPromises);
  }

  /**
   * Convertir une URL de données base64 en File
   */
  dataURLToFile(dataURL: string, filename: string): File {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  /**
   * Compresser une image avant upload
   */
  async compressImage(file: File, maxWidth: number = 1920, quality: number = 0.8): Promise<File> {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();

      img.onload = () => {
        // Calculer les nouvelles dimensions
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;

        // Dessiner l'image compressée
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        }, 'image/jpeg', quality);
      };

      img.src = URL.createObjectURL(file);
    });
  }
}

export const fileService = new FileService();